/* eslint-disable @typescript-eslint/no-explicit-any */
import { IAzureResource } from 'app/interfaces/IAzureResource';
import { ResourceId } from './resource-id';
import { Named } from 'app/model/base/named';
import { stringsAreEqualCaseInsensitive } from './string.util';
import { Metamodel } from 'app/model/base/model';
import { DatabaseEntity, ModelConstants, ChildResourceNamed } from 'shell-models-lib';
import { WranglingDataflow } from 'app/model/dataflow/wranglingDataflow';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { UserDefinedFunctionLibrary } from 'app/model/dataflow/user-defined-function-library';

export interface IResourceBase {
    name: string;
    // eslint-disable-next-line
    type: string;
    subType?: string;
    resourceType?: string;
    folderName?: string;
    tags?: any[];
    isV2?: boolean; // if it is a resource with dataset v2 features
    parentResource?: IParentResource;
    originalFileName?: string;
    // #region Fabric only
    objectId?: string;
    workspaceObjectId?: string;
    subfolderId?: number
    // #endregion
}

export interface IParentResource {
    name: string;
    resourceType: string;
}

export interface IChangeResource extends IResourceBase {
    changeType?: string;
    oldName?: string;
}
export interface INamedChange {
    changeType: string;
    named: Named;
}

export class ResourceFilesUtils {

    public static filenameToResource(fileName: string, allowDeepFilePaths?: boolean): IResourceBase | false {
        if (fileName.startsWith('/')) {
            fileName = fileName.substr(1);
        }

        if (allowDeepFilePaths == null) {
            allowDeepFilePaths = false;
        }

        // Ignoring extension and folder name including '/'
        const folderSplits = fileName.split('/');
        const directParentTypePos = folderSplits.length - 4; // the format of fileName is: [../../parentType/parentName/]type/name

        if (folderSplits.length > 2) {
            // Ignore nested files in folders other than VNets
            if (folderSplits[directParentTypePos] !== ModelConstants.managedVirtualNetwork && !allowDeepFilePaths && folderSplits[directParentTypePos] !== ModelConstants.database) {
                return false;
            }
        }
        let nameToParse = folderSplits[folderSplits.length - 1];
        const jsonExtIndex = nameToParse.indexOf('.json');

        if (jsonExtIndex > -1) {
            nameToParse = nameToParse.substring(0, jsonExtIndex);
        }

        const splits = nameToParse.split('.');
        if (splits.length > 3) {
            return false;
        }
        if (folderSplits[directParentTypePos] === ModelConstants.managedVirtualNetwork || folderSplits[directParentTypePos] === ModelConstants.database) {
            const directParentNamePos = folderSplits.length - 3;
            const typePos = folderSplits.length - 2;
            return {
                name: nameToParse,
                type: folderSplits[typePos] || '',
                parentResource: {
                    name: folderSplits[directParentNamePos],
                    resourceType: folderSplits[directParentTypePos]
                }
            };
        }

        let resourceType = '';
        switch (splits.length) {
            case 1:
                resourceType = folderSplits[0];
                break;

            case 2:
                resourceType = splits[1];
                break;

            case 3:
                resourceType = splits[2];
                break;

            default:
                break;
        }

        return {
            name: nameToParse,
            subType: splits.length === 3 ? splits[1] : undefined,
            type: resourceType
        };
    }

    /**
     * Gets the file name from an azure resource object.
     * @param resource
     */
    public static resourcePathFromAzureResource(resource: IAzureResource): string {
        const resourceId = ResourceId.parseFrom(resource.id);
        let resourceType = resourceId ? resourceId.resourceType : '';
        // Removing last character since type is in singular form for file names (pipelines -> pipeline).
        resourceType = resourceType.substring(0, resourceType.length - 1);

        const resourceSubType = (resource.properties as any).type;
        const subTypeSuffix = resourceSubType ? `.${resourceSubType}` : '';
        const typeSuffix = resourceType ? `.${resourceType}` : '';
        return `${resource.name}${subTypeSuffix}${typeSuffix}.json`;
    }

    public static publishResourcePathFromAzureResource(resource: IAzureResource): string {
        const typeSuffix = ResourceFilesUtils.resourceTypeFromAzureResource(resource);
        return `${typeSuffix}/${resource.name}.json`;
    }

    public static resourceTypeFromAzureResource(resource: IAzureResource): string {
        const resourceId = ResourceId.parseFrom(resource.id);
        let resourceType = resourceId ? resourceId.resourceType : '';
        // Removing last character since type is in singular form for file names (pipelines -> pipeline).
        resourceType = resourceType.substring(0, resourceType.length - 1);

        const typeSuffix = resourceType ? `${resourceType}` : '';
        return typeSuffix;
    }

    public static resourcePath(resource: IResourceBase, rootFolder?: string): string {
        return `${ResourceFilesUtils.resourceTypePath(resource, rootFolder)}/${resource.name}.json`;
    }

    public static resourceTypePath(resource: IResourceBase, rootFolder?: string): string {
        rootFolder = rootFolder ? `${rootFolder}/` : '';
        return `${rootFolder}${ResourceFilesUtils.resourceTypeRelativePath(resource)}`;
    }

    public static resourceTypeRelativePath(resource: IResourceBase): string {
        if (resource.parentResource) {
            return `${resource.parentResource.resourceType}/${resource.parentResource.name}/${resource.type}`;
        }
        return resource.type;
    }

    public static areSameResources(resource1?: IResourceBase, resource2?: IResourceBase): boolean {
        if (resource1 && resource2) {
            const parentInfo1 = resource1.parentResource;
            const parentInfo2 = resource2.parentResource;
            let sameParentFlag = (parentInfo1 === parentInfo2);
            if (parentInfo1 && parentInfo2) {
                sameParentFlag = (parentInfo1.name === parentInfo2.name
                    && stringsAreEqualCaseInsensitive(parentInfo1.resourceType, parentInfo2.resourceType));
            }

            return resource1.name === resource2.name
                && stringsAreEqualCaseInsensitive(resource1.type, resource2.type)
                && sameParentFlag;
        }

        return resource1 === resource2;
    }

    public static isChildResource(childResource: IResourceBase, parentResource: IResourceBase): boolean {
        if (childResource.parentResource) {
            return childResource.parentResource.name === parentResource.name
                && stringsAreEqualCaseInsensitive(childResource.parentResource.resourceType, parentResource.type);
        }

        return false;
    }

    public static namedToResourceBase(named: Named): IResourceBase {
        const model = Metamodel.modelOf(named);
        const concept = model.concept;
        const resourceBase: IResourceBase = {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            name: named.name!,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            type: concept!.name,
            subType: (model !== concept) ? model.name : undefined
        };
        // Special handling for Power Query resources which have type 'dataflow'
        if (resourceBase.type === Metamodel.modelFor(WranglingDataflow).name ||
            resourceBase.type === Metamodel.modelFor(UserDefinedFunctionLibrary).name) {
            resourceBase.type = Metamodel.modelFor(DataFlow).name;
        }
        if (named instanceof DatabaseEntity && named.parentInfo && named.parentInfo.resourceName) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            resourceBase.name = named.realName!;
            resourceBase.parentResource = {
                name: named.parentInfo.resourceName,
                resourceType: named.parentInfo.resourceType
            };
        }
        if (named instanceof ChildResourceNamed && named.parentResource) {
            const parentModel = Metamodel.modelOf(named.parentResource);
            const parentConcept = parentModel.concept;
            resourceBase.parentResource = {
                name: named.parentResource.name,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                resourceType: parentConcept!.name
            };
        }
        return resourceBase;
    }

    public static correctResourceName(resourceId: ResourceId): string {
        if (resourceId.nestedType &&
            (stringsAreEqualCaseInsensitive(resourceId.nestedType.slice(0, -1), ModelConstants.table)
                || stringsAreEqualCaseInsensitive(resourceId.nestedType.slice(0, -1), ModelConstants.relationship)
            )) {
            return `${resourceId.resourceName}$$${resourceId.nestedResouceName}`;
        }

        return (resourceId.nestedType && stringsAreEqualCaseInsensitive(resourceId.nestedType.slice(0, -1), ModelConstants.managedPrivateEndpoint))
            ? resourceId.nestedResouceName
            : resourceId.resourceName;
    }
}
